import { api } from '@/lib/api.js';

// License Manager API client. Service-layer convention: services call api.*,
// hooks call services, screens call hooks. GETs carry a cache-buster because
// the CF Pages /api/* proxy can serve a stale response after a mutation.

function qs(params = {}) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') u.set(k, v);
  u.set('_t', Date.now());
  return u.toString();
}

// ── Agency (org-wide) ──
export function getAgencyLicenses() {
  return api.get(`/api/admin/licenses/agency?${qs()}`);
}
export function getAgencyLicense(id) {
  return api.get(`/api/admin/licenses/agency/${id}?${qs()}`);
}
export function createAgencyLicense(body) {
  return api.post('/api/admin/licenses/agency', body);
}
export function updateAgencyLicense(id, body) {
  return api.put(`/api/admin/licenses/agency/${id}`, body);
}
export function deleteAgencyLicense(id) {
  return api.del(`/api/admin/licenses/agency/${id}`);
}

// ── Producer (broker-scoped; scope='all' for admin) ──
export function getProducerLicenses({ scope } = {}) {
  return api.get(`/api/admin/licenses/producer?${qs({ scope })}`);
}
export function getProducerLicense(id) {
  return api.get(`/api/admin/licenses/producer/${id}?${qs()}`);
}
export function createProducerLicense(body) {
  return api.post('/api/admin/licenses/producer', body);
}
export function updateProducerLicense(id, body) {
  return api.put(`/api/admin/licenses/producer/${id}`, body);
}
export function deleteProducerLicense(id) {
  return api.del(`/api/admin/licenses/producer/${id}`);
}

// ── Shared ──
export function searchCrmContacts(q) {
  return api.get(`/api/admin/licenses/_crm-contacts/search?${qs({ q })}`);
}
export function exportLicenses(body) {
  return api.post('/api/admin/licenses/export', body);
}

// Multipart PDF upload. The shared api wrapper hardcodes JSON, so upload uses
// raw fetch with FormData + the JWT (PolicyDocsDropSheet pattern). No
// Content-Type header — the browser sets the multipart boundary.
export async function uploadLicensePdf(kind, id, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/api/admin/licenses/${kind}/${id}/upload?_t=${Date.now()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
    body: fd,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `upload failed (HTTP ${res.status})`);
  }
  return res.json();
}

// The license PDF as a Blob, fetched with the broker JWT. The viewer used to
// point an <object> straight at the URL with no headers and rely on the CF
// Pages proxy injecting X-API-Key; since 2026-10-03 an API key through the
// proxy authorises nothing (middleware/pages-proxy.js), so the PDF is fetched
// like every other V2 document preview and shown from an object URL.
export async function getLicensePdfBlob(kind, id) {
  const res = await fetch(`/api/admin/licenses/${kind}/${id}/pdf?_t=${Date.now()}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 401) window.dispatchEvent(new Event('auth:expired'));
    throw new Error(body.error || `PDF failed to load (HTTP ${res.status})`);
  }
  return res.blob();
}
